import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { CONTACT, IMAGES, VIDEOS } from "@/data/content";
import FlowField from "@/components/FlowField";
import VideoBackdrop from "@/components/VideoBackdrop";

function OrbitRings() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute -right-40 top-1/2 hidden h-[640px] w-[640px] -translate-y-1/2 lg:block">
      <div className="animate-spin-slow absolute inset-0 rounded-full border border-dashed border-brand/25" />
      <div className="animate-spin-slower-rev absolute inset-16 rounded-full border border-brand/15" />
      <div className="animate-spin-slow absolute inset-32 rounded-full border border-dashed border-brand/20" />
      <div className="absolute left-1/2 top-0 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-brand shadow-[0_0_14px_rgb(var(--c-brand)/0.9)]" />
      <svg viewBox="0 0 24 24" className="animate-float-y absolute right-6 top-1/3 h-7 w-7 text-brand" fill="currentColor">
        <path d="M12 2l2.4 7.2H22l-6 4.6 2.3 7.2-6.3-4.5-6.3 4.5L8 13.8 2 9.2h7.6z" />
      </svg>
      <span className="hud-label absolute bottom-16 left-8">ORBIT.SYNC // 60S</span>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex items-center overflow-hidden bg-base">
      {/* slow-motion flowing water backdrop with parallax */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 h-[115%]">
        <VideoBackdrop
          sources={VIDEOS.heroWater.sources}
          poster={IMAGES.hero}
          alt="Macro view of an industrial pressure gauge in a dark calibration environment"
          rate={0.45}
          className="theme-video opacity-40"
          testId="hero-video-backdrop"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-base via-base/75 to-base/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-base via-transparent to-base/60" />
      </motion.div>

      {/* flowing water-current particles */}
      <FlowField className="opacity-80" />
      <OrbitRings />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-36 pb-20 md:px-12 md:pt-40 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 34 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl"
        >
          <div className="mb-6 flex flex-wrap items-center gap-4">
            <span data-testid="hero-eyebrow" className="hud-label border border-brand/30 bg-brand/10 px-3 py-1.5">
              ISO/IEC 17025:2017 · NGĀRUAWĀHIA, WAIKATO
            </span>
            <span data-testid="hero-accreditations" className="hud-label hidden sm:block">
              IANZ · BLUE TICK · IQP ACCREDITED
            </span>
          </div>
          <h1
            data-testid="hero-heading"
            className="font-heading text-5xl font-black uppercase leading-[0.95] tracking-tighter text-ink md:text-6xl lg:text-7xl"
          >
            IANZ-accredited instrument calibration in{" "}
            <span className="text-glow text-brand">Auckland and Hamilton</span>
          </h1>
          <p data-testid="hero-summary" className="mt-8 max-w-2xl text-base leading-relaxed text-body-strong md:text-lg">
            Pressure, temperature, flow, electrical and process instrumentation — calibrated in our Ngāruawāhia lab or on your site across Auckland and the Waikato, with traceable certificates that stand up at audit.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              asChild
              size="lg"
              data-testid="hero-quote-button"
              className="rounded-full bg-brand px-9 text-base font-semibold text-base shadow-[0_0_24px_rgb(var(--c-brand)/0.4)] transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:bg-brand-hi hover:shadow-[0_0_40px_rgb(var(--c-brand)/0.65)]"
            >
              <a href="#quote">Request a quote</a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="hero-email-button"
              className="rounded-full border-brand/40 bg-transparent px-9 text-base font-semibold text-brand transition-[transform,background-color,border-color] duration-200 hover:-translate-y-0.5 hover:border-brand hover:bg-brand/10 hover:text-brand-hi"
            >
              <a href={`mailto:${CONTACT.email}`}>
                <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
                {CONTACT.email}
              </a>
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
